/**
 * Import History: every spreadsheet loaded into the catalog, as the server
 * recorded it - which file, when, by whom, what became of its rows - searchable,
 * filterable and a page at a time. Each opens to the file's own rows
 * (ImportDetailPage), where they can be exported, imported again or deleted.
 *
 * The filters live in the address, so going into an import and back keeps them.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileSpreadsheet, RefreshCw, Search, Upload, X } from 'lucide-react';
import type { ImportHistorySort, ImportRunRow, Paginated } from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { DataTable, type Column } from '../../components/data/DataTable.js';
import { Button } from '../../components/ui/button.js';
import { Input, Select } from '../../components/ui/field.js';
import { Badge, Card, EmptyState } from '../../components/ui/primitives.js';
import { isNetworkError, listImportHistory } from '../../api/client.js';
import { useCan } from '../../auth/AuthProvider.js';
import { formatDateTime } from '../../lib/format.js';
import { MouldImportDialog } from '../moulds/MouldImportDialog.js';
import { RUN_STATUS, dayToIso, fileType, runTotals } from './importUi.js';

const PAGE_SIZE = 20;

const SORTS: Array<{ value: string; label: string; sort: ImportHistorySort; dir: 'asc' | 'desc' }> = [
  { value: 'newest', label: 'Newest first', sort: 'startedAt', dir: 'desc' },
  { value: 'oldest', label: 'Oldest first', sort: 'startedAt', dir: 'asc' },
  { value: 'name', label: 'File name A-Z', sort: 'fileName', dir: 'asc' },
  { value: 'name-desc', label: 'File name Z-A', sort: 'fileName', dir: 'desc' },
  { value: 'rows', label: 'Most rows', sort: 'rowsTotal', dir: 'desc' },
  { value: 'new', label: 'Most new moulds', sort: 'created', dir: 'desc' },
];

export function ImportsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Said by an import's own page after it was removed from the history.
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;
  const [params, setParams] = useSearchParams();
  const canImport = useCan('create', 'moulds');

  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const order = SORTS.find((s) => s.value === params.get('order')) ?? SORTS[0]!;
  const page = Math.max(1, Number(params.get('page')) || 1);

  const [search, setSearch] = useState(q);
  const [result, setResult] = useState<Paginated<ImportRunRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [importOpen, setImportOpen] = useState(false);

  /** Change some filters; any change but the page itself goes back to page 1. */
  const update = (next: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v ? p.set(k, v) : p.delete(k));
    if (!('page' in next)) p.delete('page');
    setParams(p, { replace: true });
  };

  // The search box waits for a pause in typing before it asks the server.
  useEffect(() => {
    if (search === q) return;
    const t = setTimeout(() => update({ q: search.trim() }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listImportHistory({
      q,
      status: status as ImportRunRow['status'] | '',
      from: dayToIso(from),
      to: dayToIso(to, true),
      sort: order.sort,
      dir: order.dir,
      page,
      limit: PAGE_SIZE,
    })
      .then((r) => {
        if (cancelled) return;
        setResult(r);
        setError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(isNetworkError(e) ? 'No connection. Import history is kept on the server.' : 'Could not load the import history.');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [q, status, from, to, order.sort, order.dir, page, reload]);

  const filtered = !!(q || status || from || to);
  const total = result?.pagination.total ?? 0;
  const pages = result?.pagination.totalPages ?? 1;

  const columns = useMemo<Array<Column<ImportRunRow>>>(
    () => [
      {
        key: 'file',
        header: 'File',
        primary: true,
        headerClassName: 'min-w-56',
        className: 'min-w-56 max-w-sm',
        render: (run) => (
          <div className="flex min-w-0 items-start gap-2.5">
            <FileSpreadsheet className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
            <div className="min-w-0">
              <span className="block truncate font-medium" title={run.fileName}>
                {run.fileName}
              </span>
              <span className="block truncate text-xs text-subtle-foreground">
                {fileType(run.fileName)}
                {run.sheetNames.length ? ` · ${run.sheetNames.join(', ')}` : ''}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: 'when',
        header: 'When',
        render: (run) => (
          <span className="whitespace-nowrap">
            {formatDateTime(run.startedAt)}
            <span className="block text-xs text-subtle-foreground">by {run.userName ?? 'unknown'}</span>
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        className: 'whitespace-nowrap',
        render: (run) => <Badge tone={RUN_STATUS[run.status].tone}>{RUN_STATUS[run.status].label}</Badge>,
      },
      {
        key: 'rows',
        header: 'Records',
        hideOnMobile: true,
        className: 'tabular',
        render: (run) => run.rowsTotal,
      },
      {
        key: 'imported',
        header: 'Imported',
        hideOnMobile: true,
        render: (run) => {
          const t = runTotals(run);
          return (
            <span className="whitespace-nowrap tabular">
              {t.imported}
              <span className="block text-xs text-subtle-foreground">
                {run.created} new{run.restored ? ` · ${run.restored} back` : ''}
                {run.updated ? ` · ${run.updated} filled` : ''}
              </span>
            </span>
          );
        },
      },
      {
        key: 'failed',
        header: 'Not imported',
        hideOnMobile: true,
        render: (run) => {
          const n = runTotals(run).notImported;
          return <span className={`tabular ${n ? 'font-semibold text-danger' : 'text-subtle-foreground'}`}>{n}</span>;
        },
      },
      {
        key: 'photos',
        header: 'Photos',
        hideOnMobile: true,
        render: (run) => (
          <span className="tabular">
            {run.photosAdded}
            {run.photosFailed ? <span className="block text-xs text-danger">{run.photosFailed} failed</span> : null}
          </span>
        ),
      },
      {
        key: 'location',
        header: 'Location',
        hideOnMobile: true,
        render: (run) => run.locationName ?? <span className="text-subtle-foreground">-</span>,
      },
    ],
    [],
  );

  return (
    <Page
      title="Import history"
      description={
        result
          ? `${total} imported file${total === 1 ? '' : 's'}${filtered ? ' match' : ''} - open one to see its rows, export or delete them.`
          : 'Every spreadsheet loaded into the catalog, and what became of each row.'
      }
      actions={
        <>
          <Button variant="secondary" onClick={() => setReload((n) => n + 1)} loading={loading}>
            <RefreshCw className="size-4" /> Refresh
          </Button>
          {canImport ? (
            <Button variant="primary" onClick={() => setImportOpen(true)}>
              <Upload className="size-4" /> Import a spreadsheet
            </Button>
          ) : null}
        </>
      }
    >
      <div className="mb-3 flex flex-col gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subtle-foreground" aria-hidden />
          <Input
            aria-label="Search imports"
            placeholder="Search by file name or who imported it…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            aria-label="Status"
            value={status}
            onChange={(e) => update({ status: e.target.value })}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'COMPLETED', label: 'Completed' },
              { value: 'COMPLETED_WITH_ERRORS', label: 'Completed, with problems' },
              { value: 'FAILED', label: 'Failed' },
              { value: 'RUNNING', label: 'In progress' },
            ]}
            className="h-9 w-auto min-w-48"
          />
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
            From
            <Input type="date" aria-label="From" value={from} max={to || undefined} onChange={(e) => update({ from: e.target.value })} className="h-9 w-auto" />
          </label>
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
            To
            <Input type="date" aria-label="To" value={to} min={from || undefined} onChange={(e) => update({ to: e.target.value })} className="h-9 w-auto" />
          </label>
          <Select
            aria-label="Sort"
            value={order.value}
            onChange={(e) => update({ order: e.target.value === 'newest' ? '' : e.target.value })}
            options={SORTS.map((s) => ({ value: s.value, label: s.label }))}
            className="h-9 w-auto min-w-40"
          />
          {filtered ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                update({ q: '', status: '', from: '', to: '' });
              }}
            >
              <X className="size-4" /> Clear filters
            </Button>
          ) : null}
        </div>
      </div>

      {notice ? (
        <p role="status" className="mb-3 rounded-lg bg-success-subtle px-3 py-2 text-sm text-success">
          {notice}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mb-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <Card className="overflow-hidden">
        <DataTable
          rows={result?.data}
          columns={columns}
          rowKey={(run) => run.id}
          onRowClick={(run) => navigate(`/imports/${run.id}`)}
          empty={
            <EmptyState
              icon={<FileSpreadsheet />}
              title={filtered ? 'No imports match' : 'No imports yet'}
              description={filtered ? 'Try another search, or clear the filters.' : 'Every spreadsheet imported from now on is listed here.'}
            />
          }
        />
      </Card>

      {result && total > 0 ? (
        <nav className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm" aria-label="Pages">
          <span className="text-muted-foreground">
            {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <span className="flex items-center gap-2">
            <Button variant="secondary" size="sm" disabled={page <= 1 || loading} onClick={() => update({ page: String(page - 1) })}>
              <ChevronLeft className="size-4" /> Previous
            </Button>
            <span className="tabular text-muted-foreground">
              Page {page} of {pages}
            </span>
            <Button variant="secondary" size="sm" disabled={page >= pages || loading} onClick={() => update({ page: String(page + 1) })}>
              Next <ChevronRight className="size-4" />
            </Button>
          </span>
        </nav>
      ) : null}

      <MouldImportDialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) setReload((n) => n + 1);
        }}
      />
    </Page>
  );
}
